/* am7-base.js: shared behaviour for the 7AM Club PDP sections (load with defer; every am7 section may
   include it, so it guards against running twice). Hooks (see specs/build-conventions.md):
     [data-am7-scroll-to="#id"]   smooth scroll to an element, clearing the sticky header
     [data-am7-bar]               sold/progress bar: adds .am7-is-in when visible (CSS animates the fill)
     .am7-dot                     red dot: adds .am7-is-in when visible (CSS blinks it 3 times)
     [data-am7-carousel]          scroll-snap carousel: [data-am7-track], [data-am7-prev], [data-am7-next], [data-am7-dots]
   Public API: window.AM7.scrollTo(el, opts), AM7.carousel(root) -> {goTo, next, prev, refresh, index, count},
               AM7.init(root), AM7.reducedMotion() */
(function () {
  'use strict';
  var AM7 = (window.AM7 = window.AM7 || {});
  if (AM7.__base) return;
  AM7.__base = true;

  var mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reducedMotion() {
    return !!(mqReduce && mqReduce.matches) || document.documentElement.hasAttribute('data-am7-still');
  }
  AM7.reducedMotion = reducedMotion;

  function headerHeight() {
    var v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
    return isNaN(v) ? 0 : v;
  }

  /* ---------------- 1. Smooth scroll ---------------- */
  // The theme header is "sticky on scroll up": scrolling up shows it (so clear --header-height),
  // scrolling down hides it.
  function scrollToEl(target, opts) {
    if (!target) return;
    opts = opts || {};
    var gap = typeof opts.offset === 'number' ? opts.offset : 16;
    var y = target.getBoundingClientRect().top + window.pageYOffset - gap;
    if (y < window.pageYOffset) y -= headerHeight();
    window.scrollTo({ top: Math.max(0, Math.round(y)), behavior: reducedMotion() ? 'auto' : 'smooth' });
    if (opts.focus !== false) {
      var focusable = target.matches('a[href],button,input,select,textarea,[tabindex]');
      if (!focusable) target.setAttribute('tabindex', '-1');
      try { target.focus({ preventScroll: true }); } catch (e) { /* old browsers */ }
    }
  }
  AM7.scrollTo = scrollToEl;

  document.addEventListener('click', function (e) {
    var trigger = e.target && e.target.closest ? e.target.closest('[data-am7-scroll-to]') : null;
    if (!trigger) return;
    var sel = trigger.getAttribute('data-am7-scroll-to') || trigger.getAttribute('href') || '';
    var target = null;
    try { target = sel ? document.querySelector(sel) : null; } catch (err) { target = null; }
    if (!target) return; // let the link behave normally
    e.preventDefault();
    var off = parseFloat(trigger.getAttribute('data-am7-scroll-offset'));
    scrollToEl(target, { offset: isNaN(off) ? 16 : off, focus: trigger.getAttribute('data-am7-scroll-focus') !== 'false' });
  });

  /* ---------------- 2. Bars ---------------- */
  var barIO = null;
  function showBar(el) { el.classList.add('am7-is-in'); }
  function initBars(root) {
    var bars = (root || document).querySelectorAll('[data-am7-bar]:not(.am7-is-in)');
    if (!bars.length) return;
    if (!('IntersectionObserver' in window) || reducedMotion()) {
      Array.prototype.forEach.call(bars, showBar);
      return;
    }
    if (!barIO) {
      barIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { showBar(en.target); barIO.unobserve(en.target); }
        });
      }, { threshold: 0.5, rootMargin: '0px 0px -8% 0px' });
    }
    Array.prototype.forEach.call(bars, function (b) { barIO.observe(b); });
  }
  AM7.initBars = initBars;

  /* ---------------- 2b. Red dots ---------------- */
  // .am7-dot blinks 3 times (4.2s, WCAG 2.2.2) once it is in view: CSS holds it paused until .am7-is-in.
  var dotIO = null;
  function showDot(el) { el.classList.add('am7-is-in'); }
  function initDots(root) {
    var dots = (root || document).querySelectorAll('.am7-dot:not(.am7-dot--static):not(.am7-is-in)');
    if (!dots.length) return;
    if (!('IntersectionObserver' in window) || reducedMotion()) {
      Array.prototype.forEach.call(dots, showDot);
      return;
    }
    if (!dotIO) {
      dotIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { showDot(en.target); dotIO.unobserve(en.target); }
        });
      }, { threshold: 0.5, rootMargin: '0px 0px -8% 0px' });
    }
    Array.prototype.forEach.call(dots, function (d) { dotIO.observe(d); });
  }
  AM7.initDots = initDots;

  /* ---------------- 3. Carousel ---------------- */
  // <div data-am7-carousel [data-am7-loop]>
  //   <div class="am7-track" data-am7-track> slides... </div>
  //   <button data-am7-prev>  <button data-am7-next>  <div class="am7-dots" data-am7-dots></div>
  // </div>
  // Stops = distinct scroll positions of the slides (clamped to the max scroll), so the dots match what can
  // actually be reached. Fires "am7:carousel" {index, count} on the root when the active stop changes.
  function Carousel(root) {
    this.root = root;
    this.track = root.querySelector('[data-am7-track]') || root;
    this.loop = root.hasAttribute('data-am7-loop');
    this.index = 0;
    this.stops = [0];
    var self = this;
    this._raf = 0;
    this.track.addEventListener('scroll', function () {
      if (self._raf) return;
      self._raf = requestAnimationFrame(function () { self._raf = 0; self.sync(); });
    }, { passive: true });
    root.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target : null;
      if (!t) return;
      var prev = t.closest('[data-am7-prev]'), next = t.closest('[data-am7-next]'), dot = t.closest('[data-am7-dots] > *');
      if (prev && root.contains(prev)) { e.preventDefault(); self.prev(); }
      else if (next && root.contains(next)) { e.preventDefault(); self.next(); }
      else if (dot && root.contains(dot)) {
        e.preventDefault();
        self.goTo(Array.prototype.indexOf.call(dot.parentNode.children, dot));
      }
    });
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () { self.refresh(); }).observe(this.track);
    } else {
      window.addEventListener('resize', function () { self.refresh(); });
    }
    this.refresh();
  }
  Carousel.prototype.slides = function () {
    return Array.prototype.filter.call(this.track.children, function (el) { return el.getClientRects().length > 0; });
  };
  Carousel.prototype.refresh = function () {
    var slides = this.slides(), t = this.track;
    var max = Math.max(0, t.scrollWidth - t.clientWidth);
    var stops = [];
    if (slides.length) {
      var base = slides[0].offsetLeft;
      slides.forEach(function (s) {
        var p = Math.min(Math.max(0, s.offsetLeft - base), max);
        if (!stops.length || p - stops[stops.length - 1] > 2) stops.push(p);
      });
    }
    if (!stops.length) stops = [0];
    if (max - stops[stops.length - 1] > 2) stops.push(max);
    this.stops = stops;
    this.root.classList.toggle('am7-is-static', stops.length < 2);
    this.renderDots();
    this.sync(true);
  };
  Carousel.prototype.renderDots = function () {
    var self = this;
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-am7-dots]'), function (box) {
      var n = self.stops.length;
      box.hidden = n < 2;
      if (box.children.length !== n) {
        box.innerHTML = '';
        for (var i = 0; i < n; i++) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'am7-dots__dot';
          box.appendChild(b);
        }
      }
      Array.prototype.forEach.call(box.children, function (b, i) {
        b.setAttribute('aria-label', (box.getAttribute('data-am7-dot-label') || 'Show slide') + ' ' + (i + 1) + ' of ' + n);
      });
    });
  };
  Carousel.prototype.sync = function (force) {
    var x = this.track.scrollLeft, stops = this.stops, max = this.track.scrollWidth - this.track.clientWidth;
    var idx = 0, best = Infinity;
    if (x >= max - 2) idx = stops.length - 1;
    else stops.forEach(function (p, i) { var d = Math.abs(p - x); if (d < best) { best = d; idx = i; } });
    var changed = idx !== this.index;
    this.index = idx;
    if (!changed && !force) return;
    var n = stops.length, loop = this.loop && n > 1;
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-am7-dots]'), function (box) {
      Array.prototype.forEach.call(box.children, function (b, i) {
        if (i === idx) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
      });
    });
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-am7-prev]'), function (b) {
      var off = !loop && idx === 0; b.setAttribute('aria-disabled', off ? 'true' : 'false'); b.classList.toggle('am7-is-disabled', off);
    });
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-am7-next]'), function (b) {
      var off = !loop && idx >= n - 1; b.setAttribute('aria-disabled', off ? 'true' : 'false'); b.classList.toggle('am7-is-disabled', off);
    });
    if (changed) {
      this.root.dispatchEvent(new CustomEvent('am7:carousel', { bubbles: true, detail: { index: idx, count: n } }));
    }
  };
  Carousel.prototype.goTo = function (i, instant) {
    var n = this.stops.length;
    if (this.loop) i = ((i % n) + n) % n; else i = Math.max(0, Math.min(n - 1, i));
    this.track.scrollTo({ left: this.stops[i], behavior: instant || reducedMotion() ? 'auto' : 'smooth' });
  };
  Carousel.prototype.next = function () { this.goTo(this.index + 1); };
  Carousel.prototype.prev = function () { this.goTo(this.index - 1); };
  Object.defineProperty(Carousel.prototype, 'count', { get: function () { return this.stops.length; } });

  AM7.carousel = function (root) {
    if (!root) return null;
    if (!root.__am7Carousel) root.__am7Carousel = new Carousel(root);
    return root.__am7Carousel;
  };

  /* ---------------- init ---------------- */
  function init(root) {
    root = root || document;
    initBars(root);
    initDots(root);
    Array.prototype.forEach.call(root.querySelectorAll('[data-am7-carousel]'), function (el) { AM7.carousel(el); });
  }
  AM7.init = init;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  // theme editor: re-init a section after it is re-rendered
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
